/* eslint-disable react/prop-types */
import {
  CardHeader,
  CardContent,
  CardActions,
  CardActionArea,
  Avatar,
  Typography,
  Box,
  Stack,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { StyledCard, LoadingOverlay } from "./StyledComponents";

function DocumentCard({
  doc,
  onDetailClick,
  onDeleteClick,
  isLoading,
  showDeleteButton = false,
  showCreatedAt = false,
  showBusinessIcon = true,
  t,
  translationPrefix = "myDocuments",
}) {
  const theme = useTheme();

  if (
    !doc ||
    !doc.history_steps?.length ||
    !doc.author ||
    !doc.site ||
    !doc.workflow
  ) {
    return null;
  }

  const lastStep = doc.history_steps[doc.history_steps.length - 1];
  const rawStatus = lastStep?.status;
  const rawStatusDate = lastStep?.created_at;

  const latestStatus = rawStatus
    ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
    : t(`${translationPrefix}.unknownStatus`);

  return (
    <StyledCard
      elevation={2}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          elevation: 8,
          borderColor: theme.palette.primary.main,
          transform: "translateY(-2px)",
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      }}
    >
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={32} color="primary" />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t(`${translationPrefix}.loadingDetails`)}
          </Typography>
        </LoadingOverlay>
      )}

      <CardActionArea
        onClick={() => onDetailClick(doc.id)}
        sx={{
          flexGrow: 1,
          cursor: "pointer !important",
          borderRadius: 3,
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              src={doc.author?.avatar_url}
              alt={doc.author?.username}
              sx={{
                width: 48,
                height: 48,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
          }
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showBusinessIcon && (
                <AssignmentIcon
                  sx={{
                    fontSize: 20,
                    color: theme.palette.primary.main,
                  }}
                />
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "1.1rem",
                }}
              >
                {doc.workflow?.name || t(`${translationPrefix}.noWorkflow`)}
              </Typography>
            </Box>
          }
          subheader={
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              <AccessTimeIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.text.secondary,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {convertUTCToLocal(doc.start_timestamp)}
              </Typography>
            </Box>
          }
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ pt: 0, pb: 3 }}>
          <Stack spacing={2}>
            {/* Title, Site and Author Info */}
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                {/* Title */}
                {doc.title && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AssignmentIcon
                      sx={{
                        fontSize: 18,
                        color: theme.palette.text.secondary,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {t(`${translationPrefix}.titleLabel`)}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {doc.title}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BusinessIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t(`${translationPrefix}.siteLabel`)}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {doc.site.name || t(`${translationPrefix}.unknownSite`)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t(`${translationPrefix}.createdByLabel`)}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {doc.author.first_name} {doc.author.last_name}
                  </Typography>
                </Box>

                {showCreatedAt && doc.history_steps.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon
                      sx={{
                        fontSize: 18,
                        color: theme.palette.text.secondary,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {t(`${translationPrefix}.createdAtLabel`)}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {convertUTCToLocal(doc.created_at)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Description */}
            {doc.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
              >
                &ldquo;{doc.description}&rdquo;
              </Typography>
            )}

            {/* Timeline */}
            <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {t(`${translationPrefix}.statusAtLabel`, {
                  status: latestStatus,
                })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {convertUTCToLocal(rawStatusDate)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>

      {showDeleteButton && onDeleteClick && (
        <CardActions
          sx={{
            justifyContent: "flex-end",
            px: 2,
            py: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={() => onDeleteClick(doc.id)}
            sx={{
              color: theme.palette.error.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </CardActions>
      )}
    </StyledCard>
  );
}

export default DocumentCard;
